import { Directive, ElementRef, HostListener, Input, OnInit } from "@angular/core";
import { NgControl } from "@angular/forms";
import { CurrencyFormatPipe } from "../pipe/currency-format.pipe";

@Directive({
  selector: "[currencyInputFormat]",
})
export class CurrencyInputFormatDirective implements OnInit {
  @Input() currencyFractionSize = 2;
  @Input() currencyPrefix = "$";

  private el: HTMLInputElement;

  constructor(
    private elementRef: ElementRef<HTMLInputElement>,
    private currencyFormatPipe: CurrencyFormatPipe,
    private control: NgControl
  ) {
    this.el = this.elementRef.nativeElement;
  }

  ngOnInit(): void {
    const value = this.control?.control?.value;
    if (this.isEmptyModelValue(value)) {
      return;
    }

    this.syncFromModelValue(String(value), false);
  }

  @HostListener("focus")
  onFocus(): void {
    const value = this.control?.control?.value;
    if (this.isEmptyModelValue(value)) {
      this.el.value = "";
      return;
    }
    this.el.value = this.toEditableValue(String(value));
  }

  @HostListener("blur")
  onBlur(): void {
    const value = this.control?.control?.value ?? this.el.value;
    const raw = this.toRawModelValue(String(value));

    if (raw === "") {
      this.el.value = "";
      this.setModelValue("", true);
      return;
    }

    this.syncFromModelValue(raw, true);
  }

  @HostListener("input")
  onInput(): void {
    const numeric = this.el.value.replace(/,/g, ".").replace(/[^\d.]/g, "");
    const match = numeric.match(this.getDecimalPattern());
    const clean = match?.[0] ?? "";
    if (clean !== this.el.value) {
      this.el.value = clean;
      this.el.setSelectionRange(clean.length, clean.length);
    }
    this.setModelValue(clean === "" || clean === "." ? "" : clean, true);
  }

  @HostListener("paste", ["$event"])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData("text/plain") ?? "";
    const parsed = this.toEditableValue(pasted);
    const start = this.el.selectionStart ?? this.el.value.length;
    const end = this.el.selectionEnd ?? this.el.value.length;
    this.el.setRangeText(parsed, start, end, "end");
    this.el.dispatchEvent(new Event("input", { bubbles: true }));
  }

  private syncFromModelValue(value: string, emitEvent: boolean): void {
    const raw = this.toRawModelValue(value);
    if (raw === "") {
      this.el.value = "";
      this.setModelValue("", emitEvent);
      return;
    }

    this.setModelValue(raw, emitEvent);
    this.el.value = this.formatDisplayValue(raw);
  }

  private setModelValue(value: string, emitEvent: boolean): void {
    this.control?.control?.setValue(value, {
      emitEvent,
      emitModelToViewChange: false,
    });
  }

  private formatDisplayValue(rawValue: string): string {
    const formatted = this.currencyFormatPipe.transform(rawValue, this.currencyFractionSize);
    if (!this.currencyPrefix || this.currencyPrefix === "$") {
      return formatted;
    }
    const numericPart = formatted.startsWith("$") ? formatted.slice(1) : formatted;
    return `${this.currencyPrefix}${numericPart}`;
  }

  private toEditableValue(value: string): string {
    return this.currencyFormatPipe.parse(this.stripFormatting(String(value)), this.currencyFractionSize);
  }

  private toRawModelValue(value: string): string {
    const parsed = this.toEditableValue(value);
    if (parsed === "" || parsed === ".") {
      return "";
    }
    return parsed;
  }

  private isEmptyModelValue(value: unknown): boolean {
    return value === null || value === undefined || value === "";
  }

  private stripFormatting(value: string): string {
    let normalized = value.trim();
    if (this.currencyPrefix && normalized.startsWith(this.currencyPrefix)) {
      normalized = normalized.slice(this.currencyPrefix.length);
    }
    return normalized;
  }

  private getDecimalPattern(): RegExp {
    return new RegExp(`^\\d*(?:\\.\\d{0,${this.currencyFractionSize}})?`);
  }
}
